import React, { useState, useEffect, useRef, useMemo } from 'react';

// Reusable type-to-filter dropdown, mirroring the dashboards' project
// selector (virtualized so it stays fast with thousands of options).
// Props: value, onChange, options [{value,label}], testId, placeholder,
// emptyText, disabled, className.
const ROW_HEIGHT = 36;
const OVERSCAN = 4;
const VIEWPORT_HEIGHT = 256;

const SearchableSelect = ({
  value,
  onChange,
  options = [],
  testId = 'searchable-select',
  placeholder = '-- select --',
  emptyText = 'No matches',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [filter]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || '').toLowerCase().includes(q));
  }, [options, filter]);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label || placeholder,
    [options, value, placeholder],
  );

  const totalHeight = filtered.length * ROW_HEIGHT;
  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIdx = Math.min(
    filtered.length,
    Math.ceil((scrollTop + VIEWPORT_HEIGHT) / ROW_HEIGHT) + OVERSCAN,
  );
  const visibleRows = filtered.slice(startIdx, endIdx);
  const offsetY = startIdx * ROW_HEIGHT;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className="w-full border border-gray-300 rounded px-3 py-2 text-left text-sm bg-white flex items-center justify-between focus:outline-none focus:border-[#6BADE0] focus:ring-1 focus:ring-[#6BADE0] disabled:bg-gray-100 disabled:cursor-not-allowed"
        data-testid={`${testId}-trigger`}
      >
        <span className={value ? 'text-gray-800 truncate' : 'text-gray-400'}>{selectedLabel}</span>
        <span className="text-gray-400 text-xs shrink-0 ml-2">&#9650;</span>
      </button>
      {open && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-[#6BADE0] rounded shadow-lg" data-testid={`${testId}-panel`}>
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Type to filter…"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#6BADE0]"
              autoFocus
              data-testid={`${testId}-filter-input`}
            />
          </div>
          <ul className="border-b border-gray-100">
            <li
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${value === '' ? 'bg-[#5B9BD5] text-white' : 'text-gray-400 hover:bg-[#5B9BD5] hover:text-white'}`}
              onClick={() => { onChange(''); setOpen(false); setFilter(''); }}
              data-testid={`${testId}-option-clear`}
            >
              {placeholder}
            </li>
          </ul>
          <div
            ref={listRef}
            onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
            style={{ maxHeight: VIEWPORT_HEIGHT, overflowY: 'auto' }}
            data-testid={`${testId}-viewport`}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-400 italic" data-testid={`${testId}-empty`}>
                {emptyText}
              </div>
            ) : (
              <div style={{ height: totalHeight, position: 'relative' }}>
                <ul style={{ position: 'absolute', top: 0, left: 0, right: 0, transform: `translateY(${offsetY}px)` }}>
                  {visibleRows.map((opt) => (
                    <li
                      key={opt.value}
                      style={{ height: ROW_HEIGHT, lineHeight: `${ROW_HEIGHT}px` }}
                      className={`px-3 text-sm cursor-pointer transition-colors truncate ${value === opt.value ? 'bg-[#5B9BD5] text-white' : 'text-gray-700 hover:bg-[#5B9BD5] hover:text-white'}`}
                      onClick={() => { onChange(opt.value); setOpen(false); setFilter(''); }}
                      title={opt.label}
                    >
                      {opt.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {filtered.length > 0 && (
            <div className="px-3 py-1 text-[11px] text-gray-400 border-t border-gray-100 bg-gray-50" data-testid={`${testId}-count`}>
              {filtered.length}{filter ? ` matching "${filter}"` : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
